/**
 * Verwaltungs-Präsenz – Tafel "Wer ist heute im Haus?" + Monatskalender
 * (Urlaub/Abwesenheit), gefiltert auf Verwaltungs-Mitarbeiter.
 *
 * Zielgruppe: GF/Prokurist (Berechtigung vacations:view). Status pro Mitarbeiter:
 *   • Im Haus   – aktuell eingestempelt (ggf. Pause)
 *   • Anwesend  – kein Urlaub/keine Abwesenheit, aber nicht eingestempelt
 *   • Urlaub    – heute genehmigter Urlaub
 *   • Abwesend  – heute Abwesenheit (krank etc.)
 *
 * Zusätzlicher Funktions-Filter (Standortleiter, IT, QmB, …) grenzt sowohl die
 * Tafel als auch den Monatskalender ein.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Skeleton } from '../../components/ui/skeleton';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Building2, Clock, Home, Plane, UserX, Coffee, Users } from 'lucide-react';
import api from '../../lib/api';
import { formatLastFirst } from '../../lib/nameFormat';
import TeamCalendar from '../TeamCalendar';

const STATUS_META = {
  im_haus: { label: 'Im Haus', icon: Home, badge: 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-400 border-emerald-500/30' },
  anwesend: { label: 'Anwesend', icon: Users, badge: 'bg-sky-500/15 text-sky-700 dark:text-sky-400 border-sky-500/30' },
  urlaub: { label: 'Urlaub', icon: Plane, badge: 'bg-amber-500/15 text-amber-700 dark:text-amber-400 border-amber-500/30' },
  abwesend: { label: 'Abwesend', icon: UserX, badge: 'bg-rose-500/15 text-rose-700 dark:text-rose-400 border-rose-500/30' },
};

const fmtTime = (iso) => {
  if (!iso) return null;
  try {
    return new Date(iso).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit', timeZone: 'Europe/Berlin' });
  } catch {
    return null;
  }
};

const SummaryStat = ({ statusKey, count }) => {
  const meta = STATUS_META[statusKey];
  const Icon = meta.icon;
  return (
    <div className={`flex items-center gap-2 rounded-lg border px-3 py-2 ${meta.badge}`} data-testid={`presence-stat-${statusKey}`}>
      <Icon className="h-4 w-4" />
      <span className="font-bold text-lg leading-none">{count}</span>
      <span className="text-xs font-medium">{meta.label}</span>
    </div>
  );
};

const PersonRow = ({ p }) => {
  const meta = STATUS_META[p.status];
  const Icon = meta.icon;
  const since = fmtTime(p.clock_in);
  return (
    <div
      className="flex items-center justify-between gap-3 rounded-lg border border-border/50 bg-card px-3 py-2 hover:bg-muted/40 transition-colors"
      data-testid={`presence-row-${p.id}`}
    >
      <div className="min-w-0">
        <p className="font-medium text-sm truncate">{formatLastFirst(p)}</p>
        <p className="text-xs text-muted-foreground truncate">{p.position || '—'}</p>
      </div>
      <div className="flex flex-col items-end gap-1 shrink-0">
        <Badge variant="outline" className={`gap-1 ${meta.badge}`} data-testid={`presence-badge-${p.id}`}>
          <Icon className="h-3 w-3" />
          {meta.label}
          {p.on_break && <Coffee className="h-3 w-3 ml-0.5" />}
        </Badge>
        <span className="text-[11px] text-muted-foreground">
          {p.status === 'im_haus' && since && (<><Clock className="inline h-3 w-3 mr-0.5 -mt-0.5" />seit {since}</>)}
          {p.status === 'urlaub' && (p.vacation_type || 'Urlaub')}
          {p.status === 'abwesend' && (p.absence_label || 'Abwesend')}
        </span>
      </div>
    </div>
  );
};

export default function VerwaltungPresence() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [funcFilter, setFuncFilter] = useState('all');

  useEffect(() => {
    let cancelled = false;
    api.get('/dashboard/verwaltung-presence')
      .then((res) => { if (!cancelled) setData(res.data); })
      .catch(() => { if (!cancelled) setData({ employees: [], summary: {} }); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  const people = useMemo(() => data?.employees || [], [data]);

  const functions = useMemo(() => {
    const set = new Set(people.map((p) => (p.position || '').trim()).filter(Boolean));
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [people]);

  const filtered = useMemo(() => {
    if (funcFilter === 'all') return people;
    return people.filter((p) => (p.position || '').trim() === funcFilter);
  }, [people, funcFilter]);

  const byLocation = useMemo(() => {
    const groups = {};
    filtered.forEach((p) => {
      const k = p.location_name || 'Ohne Standort';
      (groups[k] = groups[k] || []).push(p);
    });
    return Object.entries(groups).sort((a, b) => a[0].localeCompare(b[0]));
  }, [filtered]);

  const summary = useMemo(() => ({
    im_haus: filtered.filter((p) => p.status === 'im_haus').length,
    anwesend: filtered.filter((p) => p.status === 'anwesend').length,
    urlaub: filtered.filter((p) => p.status === 'urlaub').length,
    abwesend: filtered.filter((p) => p.status === 'abwesend').length,
  }), [filtered]);

  const restrictIds = useMemo(() => new Set(filtered.map((p) => p.id)), [filtered]);

  if (loading) {
    return <div className="space-y-4"><Skeleton className="h-10 w-64" /><Skeleton className="h-64 rounded-xl" /></div>;
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="verwaltung-presence">
      <Card className="border-border/60">
        <CardHeader className="pb-3">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <CardTitle className="font-heading flex items-center gap-2 text-lg">
              <Home className="h-5 w-5 text-primary" />
              Wer ist heute im Haus?
            </CardTitle>
            <div className="flex items-center gap-2">
              <Select value={funcFilter} onValueChange={setFuncFilter}>
                <SelectTrigger className="w-[220px]" data-testid="presence-function-filter">
                  <SelectValue placeholder="Alle Funktionen" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Alle Funktionen</SelectItem>
                  {functions.map((f) => (
                    <SelectItem key={f} value={f}>{f}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-5">
          <div className="flex flex-wrap gap-2">
            <SummaryStat statusKey="im_haus" count={summary.im_haus} />
            <SummaryStat statusKey="anwesend" count={summary.anwesend} />
            <SummaryStat statusKey="urlaub" count={summary.urlaub} />
            <SummaryStat statusKey="abwesend" count={summary.abwesend} />
          </div>

          {filtered.length === 0 ? (
            <p className="text-sm text-muted-foreground py-6 text-center" data-testid="presence-empty">
              Keine Verwaltungs-Mitarbeiter gefunden. Tipp: Setze bei den betreffenden Mitarbeitern die Tätigkeitsart auf „Verwaltung".
            </p>
          ) : (
            byLocation.map(([loc, list]) => (
              <div key={loc} className="space-y-2">
                <div className="flex items-center gap-2 text-sm font-semibold text-muted-foreground">
                  <Building2 className="h-4 w-4" />
                  {loc}
                  <span className="text-xs font-normal">({list.length})</span>
                </div>
                <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
                  {list.map((p) => <PersonRow key={p.id} p={p} />)}
                </div>
              </div>
            ))
          )}
        </CardContent>
      </Card>

      <div>
        <h3 className="font-heading font-semibold text-base mb-2 flex items-center gap-2">
          <Plane className="h-4 w-4 text-primary" />
          Urlaub & Abwesenheit – Monatsübersicht (Verwaltung)
        </h3>
        <TeamCalendar embedded restrictToIds={restrictIds} />
      </div>
    </div>
  );
}
